import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapPin, Compass, LocateFixed } from "lucide-react";
import { Project } from "@/types";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Button } from "./ui/button";

const DEFAULT_ZOOM = 6;

interface ProjectMapProps {
  project: Project;
  className?: string;
  showRecenterButton?: boolean;
  interactive?: boolean;
}

const ProjectMap = ({
  project,
  className,
  showRecenterButton = false,
  interactive = true,
}: ProjectMapProps) => {
  const mapboxToken =
    "pk.eyJ1IjoidHJlZS1seSIsImEiOiJja25rNG1heHgwNThjMnZwZTl2eThmeXUwIn0.SOrYUKfevZkbx8jZPxJesA";
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const [mapMoved, setMapMoved] = useState<boolean>(false);

  const initialCoordinates = useMemo<[number, number] | null>(() => {
    const hasCoordinates =
      project.geom?.type === "Point" && "coordinates" in project.geom;

    if (!hasCoordinates) return null;

    const coordinates = (project.geom as GeoJSON.Point).coordinates;
    return [coordinates[0], coordinates[1]];
  }, [project.geom]);

  mapboxgl.accessToken = mapboxToken;

  const handleRecenter = () => {
    if (map.current && initialCoordinates) {
      map.current.flyTo({
        center: initialCoordinates,
        zoom: DEFAULT_ZOOM,
        essential: true,
        duration: 1000,
      });
      setMapMoved(false);
    }
  };

  useEffect(() => {
    if (!initialCoordinates || !mapContainer.current || map.current) return;
    const [longitude, latitude] = initialCoordinates;

    const newMap = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: [longitude, latitude],
      zoom: DEFAULT_ZOOM,
      attributionControl: false,
      interactive: interactive,
      scrollZoom: interactive,
      dragRotate: interactive,
      touchZoomRotate: interactive,
      doubleClickZoom: interactive,
      dragPan: interactive,
      fadeDuration: 0,
    });

    if (interactive) {
      newMap.addControl(
        new mapboxgl.NavigationControl({
          visualizePitch: true,
          showCompass: true,
          showZoom: true,
        }),
        "top-right",
      );
    }

    const el = document.createElement("div");
    el.className = "location-indicator";
    el.style.width = "24px";
    el.style.height = "24px";
    el.style.borderRadius = "50%";
    el.style.backgroundColor = "rgba(41, 96, 217, 0.85)";
    el.style.border = "2px solid white";
    el.style.boxShadow = "0 0 0 2px rgba(41, 96, 217, 0.4)";

    const marker = new mapboxgl.Marker({
      element: el,
      anchor: "center",
    })
      .setLngLat([longitude, latitude])
      .addTo(newMap);

    markerRef.current = marker;

    if (interactive) {
      newMap.on("dragstart", () => {
        setMapMoved(true);
      });

      newMap.on("moveend", () => {
        const currentCenter = newMap.getCenter();
        const initialLng = initialCoordinates?.[0];
        const initialLat = initialCoordinates?.[1];

        if (initialLng !== undefined && initialLat !== undefined) {
          const hasMoved =
            Math.abs(currentCenter.lng - initialLng) > 0.001 ||
            Math.abs(currentCenter.lat - initialLat) > 0.001 ||
            Math.abs(newMap.getZoom() - DEFAULT_ZOOM) > 0.1;

          setMapMoved(hasMoved);
        }
      });

      const canvas = newMap.getCanvas();
      canvas.addEventListener(
        "touchstart",
        (e) => {
          if (e.touches.length === 1) {
            e.preventDefault();
          }
        },
        { passive: false },
      );
    }

    map.current = newMap;

    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
      }
      document.querySelector("style#map-pulse-animation")?.remove();
      newMap.remove();
      map.current = null;
    };
  }, [initialCoordinates, interactive]);

  if (!project.geom) {
    return (
      <div
        className={`bg-gray-100 flex items-center justify-center p-4 ${className}`}
      >
        <div className="text-center text-gray-500">
          <MapPin className="h-5 w-5 mx-auto mb-2" />
          <p className="text-sm">
            {project.countryCode
              ? `${project.location || "Unknown location"}`
              : "Location data unavailable"}
          </p>
        </div>
      </div>
    );
  }

  const hasCoordinates =
    project.geom?.type === "Point" && "coordinates" in project.geom;

  if (!hasCoordinates) {
    return (
      <div
        className={`bg-gray-100 flex items-center justify-center p-4 ${className}`}
      >
        <div className="text-center text-gray-500">
          <MapPin className="h-5 w-5 mx-auto mb-2" />
          <p className="text-sm">Unsupported geometry type</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gray-100 overflow-hidden ${className}`}>
      <div className="w-full h-full relative">
        <div ref={mapContainer} className="w-full h-full touch-manipulation" />

        {interactive && (
          <div className="absolute bottom-2 left-2 bg-white bg-opacity-75 rounded-md px-2 py-1 text-xs flex items-center">
            <Compass className="h-3 w-3 mr-1" />
            <span>Drag to pan, scroll or pinch to zoom</span>
          </div>
        )}

        {interactive && showRecenterButton && mapMoved && (
          <div className="absolute top-2 right-16">
            <Button
              onClick={handleRecenter}
              variant="outline"
              size="sm"
              className="bg-white bg-opacity-90 hover:bg-opacity-100 shadow-sm flex items-center gap-1"
            >
              <LocateFixed className="h-4 w-4" />
              <span>Recenter</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectMap;
